import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, IsoDateTime, UserRef } from '@/shared/api/types';

/*
 * Business processes API (backend module `bizproc`, route root /api/bizproc — docs/modules/bizproc.md §15).
 * Query keys start with the module id `bizproc`.
 */

/** A JSON value as stored. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type TaskView = 'open' | 'delegated' | 'done';
export type TaskKind = 'approve' | 'review' | 'request';
export type TaskRule = 'any' | 'all' | 'majority';
export type TaskStatus = 'open' | 'completed' | 'cancelled';
export type TaskOutcome = 'approved' | 'rejected' | 'done' | 'timeout' | 'cancelled';
export type AssigneeStatus =
  'waiting' | 'approved' | 'rejected' | 'reviewed' | 'submitted' | 'delegated' | 'cancelled';
export type DecisionKind = 'approve' | 'reject' | 'review' | 'submit';
export type InstanceStatus = 'running' | 'waiting' | 'faulted' | 'completed' | 'terminated';
export type InstanceFilter = 'all' | 'active' | 'waiting' | 'faulted' | 'completed' | 'terminated';

export interface DocumentRefDto {
  type: string;
  id: string;
}

// ---- tasks ----------------------------------------------------------------------------------------------------------

export interface TaskListItem {
  id: string;
  kind: TaskKind;
  title: string;
  status: TaskStatus;
  outcome: TaskOutcome | null;
  instanceId: string;
  definitionId: string;
  process: string;
  document: DocumentRefDto | null;
  documentTitle: string | null;
  canOpenDocument: boolean;
  createdAt: IsoDateTime;
  deadline: IsoDateTime | null;
  overdue: boolean;
  completedAt: IsoDateTime | null;
  myStatus: AssigneeStatus;
  myComment: string | null;
  myDecidedAt: IsoDateTime | null;
  rule: TaskRule;
  assignees: number;
  answered: number;
  initiator: UserRef | null;
  commentOnReject: boolean;
}

export interface TaskCounters {
  open: number;
  overdue: number;
}

export interface TaskOption {
  value: string;
  label: string;
}

/** A field of the document as it was when the task was made. */
export interface TaskField {
  code: string;
  label: string;
  type: string;
  multiple: boolean;
  referenceType: string | null;
  options: TaskOption[] | null;
  value: JsonValue;
}

/** A field of a request's form. */
export interface TaskFormField {
  code: string;
  label: string;
  type: string;
  multiple: boolean;
  required: boolean;
  referenceType: string | null;
  options: TaskOption[] | null;
  value: JsonValue;
}

export interface TaskAssignee {
  user: UserRef;
  status: AssigneeStatus;
  comment: string | null;
  decidedAt: IsoDateTime | null;
  delegatedFrom: UserRef | null;
  delegatedTo: UserRef | null;
  escalated: boolean;
  sort: number;
}

export interface TaskDocumentRef {
  type: string;
  id: string;
  title: string | null;
  url: string | null;
  hidden: boolean;
}

export interface TaskRefs {
  users: Record<string, UserRef>;
  files: Record<string, FileRef>;
  documents: Record<string, TaskDocumentRef>;
}

export interface TaskDetail {
  id: string;
  kind: TaskKind;
  title: string;
  description: string | null;
  status: TaskStatus;
  outcome: TaskOutcome | null;
  rule: TaskRule;
  delegation: 'none' | 'subordinates' | 'anyone';
  commentOnReject: boolean;
  onDeadline: string;
  createdAt: IsoDateTime;
  deadline: IsoDateTime | null;
  overdue: boolean;
  escalatedAt: IsoDateTime | null;
  completedAt: IsoDateTime | null;
  instanceId: string;
  definitionId: string;
  process: string;
  initiator: UserRef | null;
  document: DocumentRefDto | null;
  documentTitle: string | null;
  documentUrl: string | null;
  canOpenDocument: boolean;
  fields: TaskField[];
  form: TaskFormField[];
  assignees: TaskAssignee[];
  myStatus: AssigneeStatus | null;
  myValues: JsonValue;
  canDecide: boolean;
  canDelegate: boolean;
  canReassign: boolean;
  refs: TaskRefs;
}

export interface DecisionInput {
  decision: DecisionKind;
  comment?: string | null;
  values?: Record<string, JsonValue>;
}

// ---- instances ------------------------------------------------------------------------------------------------------

export interface InstanceListItem {
  id: string;
  definitionId: string;
  title: string;
  status: InstanceStatus;
  document: DocumentRefDto | null;
  documentTitle: string | null;
  startedBy: string | null;
  startReason: string;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  finishReason: string | null;
  currentNodes: string[];
  initiator: UserRef | null;
}

export interface NodeState {
  nodeId: string;
  status: string;
  executions: number;
  lastAt: IsoDateTime | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ExecutionInfo {
  id: string;
  nodeId: string;
  activityType: string;
  status: string;
  scope: string;
  iteration: number;
  attempt: number;
  inPort: string | null;
  outPort: string | null;
  input: JsonValue;
  output: JsonValue;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: IsoDateTime;
  completedAt: IsoDateTime | null;
}

export interface Wait {
  id: string;
  executionId: string;
  kind: 'timer' | 'document' | 'subprocess' | 'task' | 'webhook';
  key: string;
  dueAt: IsoDateTime | null;
  createdAt: IsoDateTime;
}

export interface InstanceDetail {
  id: string;
  definitionId: string;
  versionId: string;
  versionNumber: number;
  title: string;
  status: InstanceStatus;
  document: DocumentRefDto | null;
  documentTitle: string | null;
  documentUrl: string | null;
  startedBy: string | null;
  startReason: string;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  finishReason: string | null;
  finishMessage: string | null;
  parentInstanceId: string | null;
  revision: number;
  nodes: NodeState[];
  executions: ExecutionInfo[];
  waits: Wait[];
  variables: JsonValue;
  parameters: JsonValue;
  isManager: boolean;
  canTerminate: boolean;
  initiator: UserRef | null;
}

export interface HistoryItem {
  id: number;
  kind: string;
  executionId: string | null;
  nodeId: string | null;
  data: Record<string, JsonValue> | null;
  actorId: string | null;
  at: IsoDateTime;
}

export interface HistoryPage {
  items: HistoryItem[];
  nextCursor: string | null;
  users: Record<string, UserRef>;
}

export interface Incident {
  executionId: string;
  instanceId: string;
  definitionId: string;
  process: string;
  nodeId: string;
  activityType: string;
  errorCode: string | null;
  errorMessage: string | null;
  attempt: number;
  at: IsoDateTime;
  document: DocumentRefDto | null;
  ports: string[];
}

// ---- definitions ----------------------------------------------------------------------------------------------------

export interface DocumentCapabilities {
  processes: boolean;
  automation: boolean;
  statuses: boolean;
  create: boolean;
}

export interface DocumentTypeInfo {
  type: string;
  title: string;
  module: string;
  icon: string | null;
  capabilities: DocumentCapabilities;
  canManage: boolean;
}

export interface SchemaOption {
  value: string;
  label: string;
}

export interface SchemaField {
  code: string;
  label: string;
  type: string;
  multiple: boolean;
  readOnly: boolean;
  required: boolean;
  options?: SchemaOption[] | null;
  referenceType?: string | null;
  group?: string | null;
}

/** A stage (status) of a category. `semantic` is `process`, `success` or `failure`; robots and triggers may only set `settable` ones. */
export interface SchemaStatus {
  id: string;
  name: string;
  color: string | null;
  sort: number;
  semantic: 'process' | 'success' | 'failure';
  settable: boolean;
}

/** A pipeline (or the one category of a type without categories: `id` is null). */
export interface SchemaCategory {
  id: string | null;
  name: string;
  statuses: SchemaStatus[];
}

export interface DocumentSchema {
  type: string;
  title: string;
  fields: SchemaField[];
  categories: SchemaCategory[];
  capabilities: DocumentCapabilities;
  categoryLabel: string | null;
  schemaHash: string;
}

export interface DeclarationOption {
  value: string;
  label: string;
}

/** A declared start parameter, variable or constant of a process. */
export interface VariableDeclaration {
  code: string;
  label: string;
  type: string;
  multiple: boolean;
  required: boolean;
  default?: JsonValue;
  options?: DeclarationOption[] | null;
  referenceType?: string | null;
  description?: string | null;
}

export type DefinitionHealth = 'ok' | 'needsReconfirm' | 'broken';

export interface DefinitionListItem {
  id: string;
  name: string;
  description: string | null;
  kind: 'process' | 'automation';
  documentType: string;
  categoryId: string | null;
  version: number | null;
  isActive: boolean;
  health: DefinitionHealth;
  autoStart: boolean;
  running: number;
  systemCode: string | null;
  updatedAt: IsoDateTime;
  /** Only in the list of what the caller may start on a document. */
  parameters?: VariableDeclaration[] | null;
}

export interface VersionSummary {
  id: string;
  number: number;
  publishedBy: string;
  publishedAt: IsoDateTime;
  comment: string | null;
  current: boolean;
}

export interface ManualStartRule {
  enabled: boolean;
  users: string[];
  departments: string[];
  readers: boolean;
}

export interface CreateStartRule {
  enabled: boolean;
  condition?: unknown;
  includeImported: boolean;
}

export interface ChangeStartRule {
  enabled: boolean;
  fields: string[];
  condition?: unknown;
}

export interface StartRules {
  manual: ManualStartRule | null;
  onCreate: CreateStartRule | null;
  onChange: ChangeStartRule | null;
  initiatorMayTerminate: boolean;
  notifyInitiatorOfDecisions: boolean;
}

/** Where a node sits on the designer's canvas (the editor's own; the engine never reads it). */
export interface NodePosition {
  x: number;
  y: number;
}

/** A node of a process graph: one activity with its parameters (docs/modules/bizproc.md §4.2). */
export interface GraphNode {
  id: string;
  type: string;
  v?: number;
  title?: string | null;
  position?: NodePosition | null;
  /** A sticky note for the editor. */
  note?: string | null;
  params?: Record<string, ParamValue>;
  /** `incident` (default), `port` (leave through `error`) or `continue`. */
  onError?: 'incident' | 'port' | 'continue' | null;
  /** Set on the nodes the robots of a board compile to; the designer shows such graphs but never edits them. */
  robot?: string | null;
  robotRole?: string | null;
}

/** An edge from an output port of a node to a node. */
export interface GraphEdge {
  id: string;
  from: string;
  port?: string;
  to: string;
}

export interface ProcessGraph {
  schema: number;
  documentType?: string | null;
  parameters: VariableDeclaration[];
  variables: VariableDeclaration[];
  constants: VariableDeclaration[];
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface DefinitionDetail {
  id: string;
  name: string;
  description: string | null;
  kind: 'process' | 'automation';
  documentType: string;
  categoryId: string | null;
  locale: string;
  startRules: StartRules;
  constants: Record<string, JsonValue>;
  draft: ProcessGraph;
  draftRevision: string;
  draftUpdatedBy: string | null;
  draftUpdatedAt: IsoDateTime | null;
  currentVersion: number | null;
  versions: VersionSummary[];
  isActive: boolean;
  health: DefinitionHealth;
  healthDetails: JsonValue;
  systemCode: string | null;
  running: number;
  ownerId: string;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface Diagnostic {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  nodeId?: string | null;
  edgeId?: string | null;
  param?: string | null;
  args?: Record<string, string> | null;
}

export interface Validation {
  valid: boolean;
  diagnostics: Diagnostic[];
}

export interface PublishResult {
  version: number;
  created: boolean;
  warnings: Diagnostic[];
}

export interface DefinitionExport {
  format: string;
  name: string;
  description: string | null;
  documentType: string;
  kind: string;
  startRules: StartRules;
  graph: ProcessGraph;
  locale: string;
}

export interface WorkCalendar {
  days: number[];
  start: string;
  end: string;
  holidays: string[];
  workdays: string[];
}

export interface Retention {
  instances: number;
  executions: number;
  bookmarks: number;
  /** How long the log of outgoing webhook calls and of incoming deliveries is kept. */
  webhookCalls: number;
}

export interface BizprocSettings {
  workCalendar: WorkCalendar;
  retention: Retention;
  timeZone: string;
  updatedAt: IsoDateTime | null;
}

// ---- robots and triggers (docs/modules/bizproc.md §8) ---------------------------------------------------------------

/** A parameter as configured: exactly one of a literal, an expression, a text with `{{…}}` or a condition of the builder. */
export interface ParamValue {
  value?: JsonValue;
  expr?: string;
  template?: string;
  condition?: JsonValue;
}

export type DelayKind = 'immediate' | 'after' | 'at' | 'workTime';

export interface RobotDelay {
  kind: DelayKind;
  /** ISO 8601 duration of `after`. */
  duration?: string | null;
  workTime?: boolean;
  /** The date field of `at`. */
  field?: string | null;
  /** The shift of `at` (ISO 8601 with an optional sign). */
  offset?: string | null;
}

export interface Robot {
  id: string;
  statusId: string;
  type: string;
  v: number;
  title?: string | null;
  params: Record<string, ParamValue>;
  delay: RobotDelay;
  condition?: ParamValue | null;
  afterPrevious: boolean;
  onDecision?: Record<string, { statusId: string }> | null;
  onError?: 'incident' | 'continue' | null;
}

export interface BoardTrigger {
  id: string;
  code: string;
  title?: string | null;
  params: Record<string, string>;
  condition?: ParamValue | null;
  targetStatusId: string;
  allowBackwards: boolean;
}

export interface Board {
  documentType: string;
  category: string;
  version: number;
  canManage: boolean;
  robots: Robot[];
  triggers: BoardTrigger[];
  health: DefinitionHealth;
  stageHealth: Record<string, DefinitionHealth>;
  updatedAt: IsoDateTime | null;
  updatedBy: string | null;
  warnings?: Diagnostic[] | null;
  /** The name of the board's category (a pipeline, a project); null for a type without categories. */
  categoryName?: string | null;
}

export interface BoardWaiting {
  robots: Record<string, number>;
  stages: Record<string, number>;
}

export type RobotRunState = 'pending' | 'waiting' | 'done' | 'skipped' | 'cancelled' | 'failed' | 'notRun';

export interface RobotState {
  robotId: string;
  statusId: string;
  type: string;
  title: string | null;
  state: RobotRunState;
  dueAt: IsoDateTime | null;
  executionId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  taskId: string | null;
}

export interface DocumentRobots {
  documentType: string;
  documentId: string;
  statusId: string | null;
  /** The key of the board's category (empty: none) — where the stage's robots are set up. */
  category: string;
  instanceId: string | null;
  status: InstanceStatus | null;
  finishReason: string | null;
  startedAt: IsoDateTime | null;
  canManage: boolean;
  robots: RobotState[];
}

/** A parameter of an activity or a trigger as the catalog describes it. */
export interface ParameterDescriptor {
  code: string;
  labelKey: string;
  type: string;
  multiple: boolean;
  required: boolean;
  template?: boolean;
  optionsSource?: string | null;
  default?: JsonValue;
  hintKey?: string | null;
}

/** Ports a node has besides its fixed ones: a condition's branches, a parallel split's branches. */
export type DynamicPorts = 'none' | 'conditionBranches' | 'parallelBranches';

export interface ActivityOutput {
  code: string;
  labelKey: string;
  type: string;
  multiple?: boolean;
}

export interface ActivityDescriptor {
  type: string;
  version: number;
  labelKey: string;
  descriptionKey?: string | null;
  group: string;
  icon: string;
  documentTypes: string[];
  parameters: ParameterDescriptor[];
  outputs: ActivityOutput[];
  ports: string[];
  robot: boolean;
  isEngine: boolean;
  module?: string | null;
  changesDocument?: boolean;
  requiredPorts?: string[];
  dynamicPorts?: DynamicPorts;
}

/** A published version of a process: its graph, read only. */
export interface VersionDetail {
  id: string;
  definitionId: string;
  number: number;
  graph: ProcessGraph;
  touchedTypes: string[];
  publishedBy: string;
  publishedAt: IsoDateTime;
  comment: string | null;
}

/** The graph of the version an instance runs. Without `full` the parameters are left out (the viewer is no manager). */
export interface InstanceGraph {
  versionNumber: number;
  full: boolean;
  graph: ProcessGraph;
}

/** What `POST /expressions/check` answers: the type, the problems with positions, completions at the cursor. */
export interface ExpressionDiagnostic {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  start: number;
  length: number;
  args?: Record<string, string> | null;
}

export interface ExpressionCompletion {
  label: string;
  insert: string;
  kind: string;
  type?: string | null;
  detail?: string | null;
}

export interface ExpressionCheck {
  type: string | null;
  multiple: boolean;
  diagnostics: ExpressionDiagnostic[];
  completions: ExpressionCompletion[];
  replaceStart: number;
  replaceLength: number;
}

export type ExpressionKind = 'expr' | 'template' | 'condition';

export interface ExpressionCheckRequest {
  text: string;
  kind: ExpressionKind;
  documentType: string;
  definitionId?: string | null;
  nodeId?: string | null;
  cursor?: number | null;
}

export interface TriggerDescriptor {
  code: string;
  labelKey: string;
  icon: string;
  documentTypes: string[];
  parameters: ParameterDescriptor[];
}

export interface CatalogOption {
  value: string;
  label: string;
}

// ---- schedules (docs/modules/bizproc.md §23) -----------------------------------------------------------------------

export type ScheduleKind = 'daily' | 'weekly' | 'monthly' | 'interval';

/** When a schedule runs: wall-clock times of the portal's time zone. */
export interface ScheduleSpec {
  kind: ScheduleKind;
  /** `HH:mm` (daily, weekly, monthly). */
  time: string;
  /** ISO days of the week, 1 = Monday … 7 = Sunday (weekly). */
  weekdays: number[];
  /** 1–31; a month without the day runs on its last day (monthly). */
  monthDay: number;
  lastDay: boolean;
  /** The period in minutes (interval). */
  everyMinutes: number;
  workdaysOnly: boolean;
  /** `yyyy-MM-dd`. */
  startsOn: string | null;
  endsOn: string | null;
}

export type ScheduleStatus = 'active' | 'paused' | 'finished';
export type MissedPolicy = 'once' | 'skip';
export type OverlapPolicy = 'skip' | 'allow';
export type ScheduleRunStatus = 'completed' | 'limitExceeded' | 'skipped' | 'missed' | 'failed';
export type ScheduleRunKind = 'slot' | 'manual';

export interface ScheduleRunSummary {
  id: string;
  status: ScheduleRunStatus;
  reason: string | null;
  at: IsoDateTime;
  matched: number;
  started: number;
}

export interface ScheduleProblem {
  code: string;
  message: string | null;
  at: IsoDateTime;
}

export interface Schedule {
  id: string;
  definitionId: string;
  definitionName: string;
  documentType: string;
  name: string;
  categoryId: string | null;
  statusId: string | null;
  filter: ParamValue | null;
  spec: ScheduleSpec;
  parameters: Record<string, JsonValue>;
  limit: number;
  missedPolicy: MissedPolicy;
  overlapPolicy: OverlapPolicy;
  status: ScheduleStatus;
  nextSlotAt: IsoDateTime | null;
  upcoming: IsoDateTime[];
  timeZone: string;
  lastSlotAt: IsoDateTime | null;
  lastRun: ScheduleRunSummary | null;
  problem: ScheduleProblem | null;
  ownerId: string;
  owner: UserRef | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  /** The revision (`If-Match` of a change). */
  version: string;
}

export interface ScheduleInput {
  definitionId: string;
  name: string;
  categoryId: string | null;
  statusId: string | null;
  filter: ParamValue | null;
  spec: ScheduleSpec;
  parameters: Record<string, JsonValue>;
  limit: number | null;
  missedPolicy: MissedPolicy;
  overlapPolicy: OverlapPolicy;
}

export interface ScheduleRunSample {
  documentId: string;
  title: string;
  instanceId: string;
}

export interface ScheduleRun {
  id: string;
  kind: ScheduleRunKind;
  slotAt: IsoDateTime | null;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime;
  status: ScheduleRunStatus;
  reason: string | null;
  lateSeconds: number;
  missedSlots: number;
  scanned: number;
  matched: number;
  started: number;
  skipped: number;
  error: string | null;
  startedBy: string | null;
  startedByUser: UserRef | null;
  sample: ScheduleRunSample[];
}

export interface RunNowResult {
  dryRun: boolean;
  run: ScheduleRun;
}

export interface SchedulePreview {
  timeZone: string;
  slots: IsoDateTime[];
}

/** A type alias (not an interface) so that it is a set of query parameters for the HTTP client. */
export type ScheduleQuery = {
  definitionId?: string;
  documentType?: string;
};

// ---- keys and calls -------------------------------------------------------------------------------------------------

export interface TaskQuery {
  view: TaskView;
  documentType?: string;
  documentId?: string;
  definitionId?: string;
}

export interface InstanceQuery {
  status?: InstanceFilter;
  mine?: boolean;
  documentType?: string;
  documentId?: string;
  definitionId?: string;
}

export const bizprocKeys = {
  all: ['bizproc'] as const,
  tasks: () => ['bizproc', 'tasks'] as const,
  taskList: (query: TaskQuery) => ['bizproc', 'tasks', 'list', query] as const,
  counters: ['bizproc', 'tasks', 'counters'] as const,
  task: (id: string) => ['bizproc', 'tasks', 'one', id] as const,
  instances: () => ['bizproc', 'instances'] as const,
  instanceList: (query: InstanceQuery) => ['bizproc', 'instances', 'list', query] as const,
  instance: (id: string) => ['bizproc', 'instances', 'one', id] as const,
  history: (id: string) => ['bizproc', 'instances', 'history', id] as const,
  instanceGraph: (id: string) => ['bizproc', 'instances', 'graph', id] as const,
  incidents: ['bizproc', 'incidents'] as const,
  definitions: () => ['bizproc', 'definitions'] as const,
  definitionList: (documentType: string | undefined) =>
    ['bizproc', 'definitions', 'list', documentType ?? ''] as const,
  definition: (id: string) => ['bizproc', 'definitions', 'one', id] as const,
  version: (id: string, number: number) => ['bizproc', 'definitions', 'version', id, number] as const,
  startable: (documentType: string, documentId: string) =>
    ['bizproc', 'definitions', 'startable', documentType, documentId] as const,
  documentTypes: ['bizproc', 'catalog', 'document-types'] as const,
  schema: (type: string) => ['bizproc', 'catalog', 'schema', type] as const,
  settings: ['bizproc', 'settings'] as const,
  automation: () => ['bizproc', 'automation'] as const,
  board: (type: string, category: string) => ['bizproc', 'automation', 'board', type, category] as const,
  boardWaiting: (type: string, category: string) =>
    ['bizproc', 'automation', 'waiting', type, category] as const,
  documentRobots: (type: string, id: string) => ['bizproc', 'automation', 'document', type, id] as const,
  robotActivities: (type: string) => ['bizproc', 'catalog', 'robots', type] as const,
  activities: (type: string) => ['bizproc', 'catalog', 'activities', type] as const,
  triggerCatalog: (type: string) => ['bizproc', 'catalog', 'triggers', type] as const,
  options: (source: string) => ['bizproc', 'catalog', 'options', source] as const,
  schedules: () => ['bizproc', 'schedules'] as const,
  scheduleList: (query: ScheduleQuery) => ['bizproc', 'schedules', 'list', query] as const,
  scheduleRuns: (id: string) => ['bizproc', 'schedules', 'runs', id] as const,
};

const enc = encodeURIComponent;

export const bizprocApi = {
  tasks: (query: TaskQuery, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<TaskListItem>>('/bizproc/tasks', {
      query: { ...query, cursor, limit: 30 },
      signal,
    }),
  counters: (signal?: AbortSignal) => api.get<TaskCounters>('/bizproc/tasks/counters', { signal }),
  task: (id: string, signal?: AbortSignal) => api.get<TaskDetail>(`/bizproc/tasks/${enc(id)}`, { signal }),
  decide: (id: string, input: DecisionInput) => api.post(`/bizproc/tasks/${enc(id)}/decision`, input),
  delegate: (id: string, userId: string, comment: string | null) =>
    api.post(`/bizproc/tasks/${enc(id)}/delegate`, { userId, comment }),
  reassign: (id: string, fromUserId: string, toUserId: string) =>
    api.post(`/bizproc/tasks/${enc(id)}/reassign`, { fromUserId, toUserId }),

  instances: (query: InstanceQuery, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<InstanceListItem>>('/bizproc/instances', {
      query: { ...query, status: query.status === 'all' ? undefined : query.status, cursor, limit: 30 },
      signal,
    }),
  instance: (id: string, signal?: AbortSignal) =>
    api.get<InstanceDetail>(`/bizproc/instances/${enc(id)}`, { signal }),
  history: (id: string, signal?: AbortSignal) =>
    api.get<HistoryPage>(`/bizproc/instances/${enc(id)}/history`, { query: { limit: 500 }, signal }),
  instanceGraph: (id: string, signal?: AbortSignal) =>
    api.get<InstanceGraph>(`/bizproc/instances/${enc(id)}/graph`, { signal }),
  terminate: (id: string, reason: string | null) =>
    api.post(`/bizproc/instances/${enc(id)}/terminate`, { reason }),
  start: (definitionId: string, document: DocumentRefDto, parameters: Record<string, JsonValue>) =>
    api.post<{ instanceId: string }>('/bizproc/instances', { definitionId, document, parameters }),

  incidents: (signal?: AbortSignal) => api.get<Incident[]>('/bizproc/incidents', { signal }),
  retry: (executionId: string) => api.post(`/bizproc/executions/${enc(executionId)}/retry`),
  skip: (executionId: string, port: string) =>
    api.post(`/bizproc/executions/${enc(executionId)}/skip`, { port, outputs: {} }),

  documentTypes: (signal?: AbortSignal) =>
    api.get<DocumentTypeInfo[]>('/bizproc/catalog/document-types', { signal }),
  documentSchema: (type: string, signal?: AbortSignal) =>
    api.get<DocumentSchema>(`/bizproc/catalog/document-types/${enc(type)}/schema`, { signal }),
  definitions: (documentType: string | undefined, signal?: AbortSignal) =>
    api.get<DefinitionListItem[]>('/bizproc/definitions', { query: { documentType }, signal }),
  startable: (documentType: string, documentId: string, signal?: AbortSignal) =>
    api.get<DefinitionListItem[]>('/bizproc/definitions', {
      query: { documentType, forDocument: documentId },
      signal,
    }),
  definition: (id: string, signal?: AbortSignal) =>
    api.get<DefinitionDetail>(`/bizproc/definitions/${enc(id)}`, { signal }),
  createDefinition: (input: { documentType: string; name: string; description?: string | null }) =>
    api.post<DefinitionDetail>('/bizproc/definitions', input),
  updateDefinition: (id: string, input: { name?: string; description?: string; isActive?: boolean }) =>
    api.patch<DefinitionDetail>(`/bizproc/definitions/${enc(id)}`, input),
  deleteDefinition: (id: string) => api.delete(`/bizproc/definitions/${enc(id)}`),
  /** Saves the whole draft; `revision` is the one it was loaded at (`If-Match`), null saves over whatever is there. */
  saveDraft: (id: string, graph: ProcessGraph, revision: string | null) =>
    api.put<{ draftRevision: string; savedAt: IsoDateTime }>(`/bizproc/definitions/${enc(id)}/draft`, graph, {
      headers: revision === null ? undefined : { 'If-Match': revision },
    }),
  setConstants: (id: string, values: Record<string, JsonValue>) =>
    api.put<DefinitionDetail>(`/bizproc/definitions/${enc(id)}/constants`, { values }),
  /** The draft's diagnostics: the same checks as on publication, with the caller's rights. */
  validate: (id: string, signal?: AbortSignal) =>
    api.post<Validation>(`/bizproc/definitions/${enc(id)}/validate`, undefined, { signal }),
  version: (id: string, number: number, signal?: AbortSignal) =>
    api.get<VersionDetail>(`/bizproc/definitions/${enc(id)}/versions/${number}`, { signal }),
  checkExpression: (request: ExpressionCheckRequest, signal?: AbortSignal) =>
    api.post<ExpressionCheck>('/bizproc/expressions/check', request, { signal }),
  publish: (id: string, comment: string | null) =>
    api.post<PublishResult>(`/bizproc/definitions/${enc(id)}/publish`, { comment }),
  exportDefinition: (id: string, signal?: AbortSignal) =>
    api.get<DefinitionExport>(`/bizproc/definitions/${enc(id)}/export`, { signal }),
  importDefinition: (input: { definition: DefinitionExport; documentType?: string; name?: string }) =>
    api.post<DefinitionDetail>('/bizproc/definitions/import', input),
  duplicate: (id: string) => api.post<DefinitionDetail>(`/bizproc/definitions/${enc(id)}/duplicate`),
  reconfirm: (id: string) => api.post<DefinitionDetail>(`/bizproc/definitions/${enc(id)}/reconfirm`),
  startRules: (id: string, rules: StartRules) =>
    api.put<DefinitionDetail>(`/bizproc/definitions/${enc(id)}/start-rules`, rules),

  board: (type: string, category: string, signal?: AbortSignal) =>
    api.get<Board>(`/bizproc/automation/${enc(type)}`, { query: { category }, signal }),
  saveBoard: (
    type: string,
    category: string,
    body: { robots: Robot[]; triggers: BoardTrigger[] },
    version: number,
  ) =>
    api.put<Board>(`/bizproc/automation/${enc(type)}?category=${enc(category)}`, body, {
      headers: { 'If-Match': `"${version}"` },
    }),
  reconfirmBoard: (type: string, category: string) =>
    api.post<Board>(`/bizproc/automation/${enc(type)}/reconfirm?category=${enc(category)}`),
  boardWaiting: (type: string, category: string, signal?: AbortSignal) =>
    api.get<BoardWaiting>(`/bizproc/automation/${enc(type)}/waiting`, { query: { category }, signal }),
  documentRobots: (type: string, id: string, signal?: AbortSignal) =>
    api.get<DocumentRobots>(`/bizproc/automation/${enc(type)}/documents/${enc(id)}`, { signal }),
  robotActivities: (type: string, signal?: AbortSignal) =>
    api.get<ActivityDescriptor[]>('/bizproc/catalog/activities', {
      query: { documentType: type, robots: true },
      signal,
    }),
  /** Every activity that works with documents of a type: the designer's palette. */
  activities: (type: string, signal?: AbortSignal) =>
    api.get<ActivityDescriptor[]>('/bizproc/catalog/activities', { query: { documentType: type }, signal }),
  triggerCatalog: (type: string, signal?: AbortSignal) =>
    api.get<TriggerDescriptor[]>('/bizproc/catalog/triggers', { query: { documentType: type }, signal }),
  options: (source: string, signal?: AbortSignal) =>
    api.get<CatalogOption[]>(`/bizproc/catalog/options/${enc(source)}`, { signal }),

  schedules: (query: ScheduleQuery, signal?: AbortSignal) =>
    api.get<Schedule[]>('/bizproc/schedules', { query, signal }),
  createSchedule: (input: ScheduleInput) => api.post<Schedule>('/bizproc/schedules', input),
  updateSchedule: (id: string, input: ScheduleInput, version: string) =>
    api.put<Schedule>(`/bizproc/schedules/${enc(id)}`, input, { headers: { 'If-Match': `"${version}"` } }),
  deleteSchedule: (id: string) => api.delete(`/bizproc/schedules/${enc(id)}`),
  pauseSchedule: (id: string) => api.post<Schedule>(`/bizproc/schedules/${enc(id)}/pause`),
  resumeSchedule: (id: string) => api.post<Schedule>(`/bizproc/schedules/${enc(id)}/resume`),
  runSchedule: (id: string, dryRun: boolean) =>
    api.post<RunNowResult>(`/bizproc/schedules/${enc(id)}/run`, { dryRun }),
  scheduleRuns: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<ScheduleRun>>(`/bizproc/schedules/${enc(id)}/runs`, {
      query: { cursor, limit: 20 },
      signal,
    }),
  scheduleRun: (id: string, runId: string, signal?: AbortSignal) =>
    api.get<ScheduleRun>(`/bizproc/schedules/${enc(id)}/runs/${enc(runId)}`, { signal }),
  previewSchedule: (spec: ScheduleSpec, signal?: AbortSignal) =>
    api.post<SchedulePreview>('/bizproc/schedules/preview', { spec }, { signal }),

  settings: (signal?: AbortSignal) => api.get<BizprocSettings>('/bizproc/settings', { signal }),
  saveSettings: (input: { workCalendar: WorkCalendar; retention: Retention }) =>
    api.put<BizprocSettings>('/bizproc/settings', input),
};

// ---- hooks ----------------------------------------------------------------------------------------------------------

/** The sidebar's counter and the tab badges: how many tasks wait for the user (refreshed by `bizproc.tasks-changed`). */
export function useTaskCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.counters,
    queryFn: ({ signal }) => bizprocApi.counters(signal),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Pages of "My tasks": full pages by cursor (the server filters by rights before cutting a page). */
export function useTasks(query: TaskQuery, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: bizprocKeys.taskList(query),
    queryFn: ({ pageParam, signal }) => bizprocApi.tasks(query, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: bizprocKeys.task(id ?? ''),
    queryFn: ({ signal }) => bizprocApi.task(id ?? '', signal),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useInstances(query: InstanceQuery, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: bizprocKeys.instanceList(query),
    queryFn: ({ pageParam, signal }) => bizprocApi.instances(query, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

export function useInstance(id: string | undefined) {
  return useQuery({
    queryKey: bizprocKeys.instance(id ?? ''),
    queryFn: ({ signal }) => bizprocApi.instance(id ?? '', signal),
    enabled: !!id,
    staleTime: 10_000,
  });
}

/** The graph of the version an instance runs (the schema's canvas); the server redacts the parameters for non-managers. */
export function useInstanceGraph(id: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.instanceGraph(id ?? ''),
    queryFn: ({ signal }) => bizprocApi.instanceGraph(id ?? '', signal),
    enabled: !!id && (options.enabled ?? true),
    // A version never changes: the graph is fetched once per instance.
    staleTime: Infinity,
  });
}

export function useInstanceHistory(id: string | undefined) {
  return useQuery({
    queryKey: bizprocKeys.history(id ?? ''),
    queryFn: ({ signal }) => bizprocApi.history(id ?? '', signal),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useIncidents(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.incidents,
    queryFn: ({ signal }) => bizprocApi.incidents(signal),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

/** Document types with what processes may do and whether the user manages their processes. */
export function useDocumentTypes(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.documentTypes,
    queryFn: ({ signal }) => bizprocApi.documentTypes(signal),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/** The fields of a document type (the start rules name the fields whose change starts a process). */
export function useDocumentSchema(type: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.schema(type ?? ''),
    queryFn: ({ signal }) => bizprocApi.documentSchema(type ?? '', signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

export function useDefinitions(documentType: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.definitionList(documentType),
    queryFn: ({ signal }) => bizprocApi.definitions(documentType, signal),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useDefinition(id: string | undefined) {
  return useQuery({
    queryKey: bizprocKeys.definition(id ?? ''),
    queryFn: ({ signal }) => bizprocApi.definition(id ?? '', signal),
    enabled: !!id,
    staleTime: 10_000,
  });
}

/** A published version of a process (managers of its type). */
export function useVersion(
  id: string | undefined,
  number: number | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: bizprocKeys.version(id ?? '', number ?? 0),
    queryFn: ({ signal }) => bizprocApi.version(id ?? '', number ?? 0, signal),
    enabled: !!id && number !== null && (options.enabled ?? true),
    staleTime: Infinity,
  });
}

/** What the user may start by hand on a document, with the declared start parameters. */
export function useStartable(documentType: string, documentId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.startable(documentType, documentId),
    queryFn: ({ signal }) => bizprocApi.startable(documentType, documentId, signal),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useBizprocSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.settings,
    queryFn: ({ signal }) => bizprocApi.settings(signal),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

/** The robots and triggers of a document type's category (managers of the type only: 403 for everybody else). */
export function useBoard(type: string | undefined, category: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.board(type ?? '', category),
    queryFn: ({ signal }) => bizprocApi.board(type ?? '', category, signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 15_000,
    retry: false,
  });
}

/** How many documents wait for each robot of the board (the warning before robots are removed). */
export function useBoardWaiting(
  type: string | undefined,
  category: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: bizprocKeys.boardWaiting(type ?? '', category),
    queryFn: ({ signal }) => bizprocApi.boardWaiting(type ?? '', category, signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 10_000,
    retry: false,
  });
}

/** The robots of the stage a document is in and what became of them (the «Robots» of the document's tab). */
export function useDocumentRobots(type: string, id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.documentRobots(type, id),
    queryFn: ({ signal }) => bizprocApi.documentRobots(type, id, signal),
    enabled: options.enabled ?? true,
    staleTime: 10_000,
    retry: false,
  });
}

/** Activities that may be robots on documents of a type. */
export function useRobotActivities(type: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.robotActivities(type ?? ''),
    queryFn: ({ signal }) => bizprocApi.robotActivities(type ?? '', signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/** Every activity applicable to documents of a type (the designer's palette and properties). */
export function useActivities(type: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.activities(type ?? ''),
    queryFn: ({ signal }) => bizprocApi.activities(type ?? '', signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/** Triggers that may be set on documents of a type. */
export function useTriggerCatalog(type: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.triggerCatalog(type ?? ''),
    queryFn: ({ signal }) => bizprocApi.triggerCatalog(type ?? '', signal),
    enabled: !!type && (options.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/** Options of a list parameter (pipelines, activity types…), as the user may pick them. */
export function useCatalogOptions(source: string | null | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.options(source ?? ''),
    queryFn: ({ signal }) => bizprocApi.options(source ?? '', signal),
    enabled: !!source && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

/** The schedules the user manages (of a process, of a document type, or all). */
export function useSchedules(query: ScheduleQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bizprocKeys.scheduleList(query),
    queryFn: ({ signal }) => bizprocApi.schedules(query, signal),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
  });
}

/** The history of a schedule's runs, newest first, by cursor. */
export function useScheduleRuns(id: string | undefined, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: bizprocKeys.scheduleRuns(id ?? ''),
    queryFn: ({ pageParam, signal }) => bizprocApi.scheduleRuns(id ?? '', pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id && (options.enabled ?? true),
    staleTime: 10_000,
  });
}

/** Flattens the pages of an infinite query. */
export function flatPages<T>(data: { pages: CursorPage<T>[] } | undefined): T[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

// ---- paths ----------------------------------------------------------------------------------------------------------

export const instancePath = (id: string) => `/bizproc/instances/${id}`;
export const definitionPath = (id: string) => `/bizproc/definitions/${id}`;
export const tasksPath = '/bizproc/tasks';

/**
 * The board of a document type's category: the CRM's types in the CRM section «Роботы» (`/crm/automation`), the types of other modules
 * (the tasks of a project) on the page `/bizproc/automation/<type>`.
 */
export const automationPath = (type: string, category?: string | null) =>
  type.startsWith('crm.')
    ? `/crm/automation?type=${encodeURIComponent(type)}${category ? `&category=${encodeURIComponent(category)}` : ''}`
    : `/bizproc/automation/${encodeURIComponent(type)}${category ? `?category=${encodeURIComponent(category)}` : ''}`;
